<template>
  <div class="flex w-full flex-col gap-4">
    <div class="-m-1 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-1">
      <div class="shrink-0">
        <WizardProgressBar
          v-model:step="localStep"
          :steps="stepsForProgressBar"
          size="extra-small"
        />
      </div>
      <div
        v-if="payment"
        class="relative flex min-h-96 flex-1 flex-col gap-4 overflow-y-auto"
      >
        <div v-if="step === 0">
          <SelectType
            v-model:payment="localPayment"
            :custom-payment-modes="customPaymentModes"
            :has-dossier="hasDossier"
            :errors="errors"
          />
        </div>
        <div v-else-if="step === 1">
          <SelectIssuer
            v-model:payment="localPayment"
            :dossier-contacts="dossierContacts"
            :place-holder-issuer="placeHolderIssuer"
            :place-holder-direct-debit="placeHolderDirectDebit"
            :errors="errors"
          />
        </div>
        <div v-else-if="step === 2">
          <SelectAmount
            v-model:payment="localPayment"
            :errors="errors"
            :default-bank-account-id="defaultBankAccountId"
            :currency="currency"
          />
        </div>
        <div v-else-if="step === 3">
          <SelectOtherInformations
            v-model:payment="localPayment"
            :errors="errors"
          >
            <template #other-informations>
              <slot name="other-informations" />
            </template>
          </SelectOtherInformations>
        </div>
      </div>
    </div>
    <div class="flex shrink-0 flex-row justify-end gap-2">
      <BaseButton
        v-if="step > 0"
        :title="$t('button.previous')"
        icon="arrow-left"
        color="secondary"
        inverse-icon-order
        @click="localStep--"
      />
      <BaseButton
        v-if="step < stepsForProgressBar.length - 1"
        :title="$t('button.next')"
        color="secondary"
        icon="arrow-right"
        :disabled="!stepValid"
        @click="localStep++"
      />
      <BaseButton
        :title="$t('button.save')"
        :icon="'paper-plane'"
        :display-loader="formSubmitting"
        :disabled="formSubmitting || !allIsValid"
        color="primary"
        @click="$emit('submit:payment')"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import SelectOtherInformations from '@ui/modal/payment/components/SelectOtherInformations.vue'
import SelectType from '@ui/modal/payment/components/SelectType.vue'
import SelectAmount from '@ui/modal/payment/components/SelectAmount.vue'
import SelectIssuer from '@ui/modal/payment/components/SelectIssuer.vue'
import WizardProgressBar from '@ui/wizard/WizardProgressBar.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { mapGetters } from 'vuex'

export default defineComponent({
  name: 'PaymentModalWizard',
  components: {
    BaseButton,
    WizardProgressBar,
    SelectIssuer,
    SelectAmount,
    SelectType,
    SelectOtherInformations
  },
  props: {
    payment: {
      type: Object,
      required: true
    },
    step: {
      type: Number,
      required: true
    },
    placeHolderIssuer: {
      type: String,
      required: true
    },
    placeHolderDirectDebit: {
      type: String,
      required: true
    },
    dossierContacts: {
      type: Array,
      required: true
    },
    formSubmitting: {
      type: Boolean,
      required: true
    },
    customPaymentModes: {
      type: Array,
      required: true
    },
    hasDossier: {
      type: Boolean,
      required: true
    },
    withOtherSection: {
      type: Boolean,
      required: false,
      default: true
    },
    funeralHomeId: {
      type: String,
      required: false,
      default: null
    },
    currency: {
      type: String,
      required: true
    }
  },
  emits: ['update:payment', 'update:step', 'submit:payment'],
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth',
      errors: 'auth/getErrors',
      initialMetadata: 'metadata/getInitialMetadata',
      payment_modes: 'metadata/getPaymentModesMetadata',
      invoice: 'invoicing/getInvoice'
    }),
    localPayment: {
      get() {
        return this.payment
      },
      set(value) {
        this.$emit('update:payment', value)
      }
    },
    localStep: {
      get() {
        return this.step
      },
      set(value) {
        this.$emit('update:step', value)
      }
    },
    stepsForProgressBar() {
      return this.stepsList.map((step, idx) => {
        return {
          ...step,
          id: idx,
          status: step.valid
            ? 'complete'
            : idx === this.step
              ? 'current'
              : 'upcoming'
        }
      })
    },
    stepsList() {
      let steps = [
        {
          name:
            this.payment?.payment_mode && this.selectedPaymentMode
              ? this.selectedPaymentMode.name
              : this.$t('attributes.payment_mode'),
          valid:
            this.payment?.payment_mode !== null &&
            (this.customPaymentModes.length === 0 ||
              this.payment?.payment_mode_id !== null)
        },
        {
          name: this.$t('attributes.issuer'),
          valid:
            this.payment?.payment_mode === 'LEV' ||
            (this.payment?.tier_payant === 1 &&
              !!this.payment?.payer_type &&
              (!!this.payment?.payer_id || !!this.payment?.issuer_name)) ||
            (this.payment?.tier_payant === 0 &&
              !!this.payment?.issuer_name?.length)
        },
        {
          name: this.$t('invoicing.amount'),
          valid: this.payment?.amount !== null
        }
      ]

      if (this.withOtherSection) {
        steps.push({
          name: this.$t('attributes.other_data'),
          valid: false
        })
      }

      return steps
    },
    stepValid() {
      return this.stepsForProgressBar[this.step].valid
    },
    allIsValid() {
      return this.stepsList
        .filter((step) => step.name !== this.$t('attributes.other_data'))
        .every((step) => step.valid === true)
    },
    selectedPaymentMode() {
      return this.payment_modes.find(
        (payment_mode) => this.payment.payment_mode === payment_mode.id
      )
    },
    /**
     * Returns the default bank account id of the funeral home (agency) related to the invoice or dossier if it exists
     * @return {string|null}
     */
    defaultBankAccountId() {
      const funeralHomeId = this.funeralHomeId ?? this.invoice?.funeral_home_id
      return (
        this.initialMetadata.models?.funeralHomes?.find(
          (funeralHome) => funeralHome.id === funeralHomeId
        )?.default_bank_account_id ?? null
      )
    }
  }
})
</script>
